<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12" xl="12">
        <transition name="slide">
          <b-card>
            <template #header>
              <h5 class="m-0">Gerenciar Cursos</h5>
            </template>

            <b-card-body>
              <!-- Filtros -->
              <div class="filters-section">
                <b-form-group label="Pesquisar" class="search-filter">
                  <b-input-group>
                    <b-input-group-prepend>
                      <b-input-group-text>
                        <i class="fas fa-search" aria-hidden="true"></i>
                      </b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="search"
                      type="search"
                      placeholder="Buscar por nome ou categoria..."
                      @input="debouncedSearch"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button :disabled="!search" @click="clearSearch"
                        >Limpar</b-button
                      >
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>

                <b-form-group label="Categoria" class="category-filter">
                  <b-form-select
                    v-model="filterCategory"
                    :options="categoryOptions"
                    @change="loadCourses"
                  ></b-form-select>
                </b-form-group>

                <div v-if="canManage" class="action-button">
                  <b-button size="sm" variant="primary" @click="handleAdd">
                    <i class="fa fa-plus mr-1"></i>
                    Novo Curso
                  </b-button>
                </div>
              </div>

              <!-- Tabela de Cursos -->
              <div class="table-container">
                <b-table
                  :items="filteredCourses"
                  :fields="fields"
                  :busy="loading"
                  thead-class="courses-header"
                  :tbody-tr-class="rowClass"
                  borderless
                  hover
                  responsive
                  show-empty
                  :per-page="perPage"
                  :current-page="currentPage"
                >
                  <template #table-busy>
                    <div class="text-center py-5">
                      <b-spinner></b-spinner>
                      <span class="ml-2">Carregando...</span>
                    </div>
                  </template>

                  <template #empty>
                    <div class="text-muted text-center py-5">
                      <i class="fas fa-graduation-cap fa-2x"></i>
                      <p class="mt-3">Nenhum curso encontrado</p>
                    </div>
                  </template>

                  <template #cell(nome)="data">
                    <strong class="course-name">{{ data.value }}</strong>
                  </template>

                  <template #cell(subtitulo)="data">
                    <div
                      class="subtitle-cell"
                      v-b-tooltip.hover
                      :title="data.value"
                    >
                      {{ data.value || "-" }}
                    </div>
                  </template>

                  <template #cell(categoria)="data">
                    <span class="category-text">{{ data.value || "-" }}</span>
                  </template>

                  <template #cell(createdBy)="data">
                    <span class="creator-text">
                      {{ formatCreatedBy(data.value) }}
                    </span>
                  </template>

                  <template #cell(actions)="data">
                    <div class="action-buttons">
                      <button
                        type="button"
                        class="action-btn action-btn-secondary"
                        @click.stop="handleConfigure(data.item)"
                        v-b-tooltip.hover
                        title="Configurar"
                      >
                        <i class="fa fa-cog"></i>
                      </button>
                      <button
                        v-if="canManage"
                        type="button"
                        class="action-btn action-btn-primary"
                        @click.stop="handleEdit(data.item)"
                        v-b-tooltip.hover
                        title="Editar"
                      >
                        <i class="fa fa-edit"></i>
                      </button>
                      <button
                        v-if="canManage"
                        type="button"
                        class="action-btn action-btn-danger"
                        @click.stop="confirmDelete(data.item)"
                        v-b-tooltip.hover
                        title="Deletar"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  </template>
                </b-table>
              </div>

              <!-- Paginação -->
              <nav class="pagination-nav">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalCourses"
                  :per-page="perPage"
                  prev-text="Anterior"
                  next-text="Próximo"
                  align="center"
                ></b-pagination>
              </nav>
            </b-card-body>
          </b-card>
        </transition>
      </b-col>
    </b-row>

    <!-- Modal de Adicionar/Editar Curso -->
    <b-modal
      v-model="modalVisible"
      :title="editingCourse ? 'Editar Curso' : 'Adicionar Novo Curso'"
      size="lg"
      @hidden="resetForm"
      @ok="handleSubmit"
      ok-title="Salvar"
      cancel-title="Cancelar"
      :ok-disabled="loading"
    >
      <b-form @submit.prevent="handleSubmit">
        <b-form-group
          label="Nome do Curso"
          label-for="nome"
          :invalid-feedback="formErrors.nome"
          :state="!formErrors.nome"
        >
          <b-form-input
            id="nome"
            v-model="form.nome"
            placeholder="Ex: Desenvolvimento Web Completo"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group
          label="Subtítulo"
          label-for="subtitulo"
          :invalid-feedback="formErrors.subtitulo"
          :state="!formErrors.subtitulo"
        >
          <b-form-input
            id="subtitulo"
            v-model="form.subtitulo"
            placeholder="Ex: Aprenda HTML, CSS, JavaScript e muito mais"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group
          label="Descrição"
          label-for="descricao"
          :invalid-feedback="formErrors.descricao"
          :state="!formErrors.descricao"
        >
          <b-form-textarea
            id="descricao"
            v-model="form.descricao"
            placeholder="Descrição detalhada do curso..."
            rows="4"
            required
          ></b-form-textarea>
        </b-form-group>

        <b-form-group
          label="URL da Imagem"
          label-for="imagem"
          :invalid-feedback="formErrors.imagem"
          :state="!formErrors.imagem"
          description="Cole a URL de uma imagem para o curso"
        >
          <b-form-input
            id="imagem"
            v-model="form.imagem"
            type="url"
            placeholder="https://exemplo.com/imagem.jpg"
            required
          ></b-form-input>
        </b-form-group>

        <b-row>
          <b-col md="6">
            <b-form-group
              label="Categoria"
              label-for="categoria"
              :invalid-feedback="formErrors.categoria"
              :state="!formErrors.categoria"
            >
              <b-form-select
                id="categoria"
                v-model="form.categoria"
                :options="categorias"
                required
              ></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Tags"
              label-for="tags"
              description="Digite as tags e pressione Enter"
            >
              <b-form-tags
                id="tags"
                v-model="form.tags"
                placeholder="Ex: JavaScript, React, Node.js"
                tag-variant="success"
                tag-pills
              ></b-form-tags>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group
          label="Validade do Certificado"
          label-for="certificateValidity"
          description="Período de validade do certificado após conclusão"
          :invalid-feedback="formErrors.certificateValidity"
          :state="!formErrors.certificateValidity"
        >
          <b-form-select
            id="certificateValidity"
            v-model="form.certificateValidity"
            :options="validityOptions"
            required
          ></b-form-select>
        </b-form-group>

        <!-- Preview da Imagem -->
        <b-form-group v-if="form.imagem" label="Preview">
          <b-img
            :src="form.imagem"
            fluid
            rounded
            alt="Preview do curso"
            @error="imageError = true"
          ></b-img>
          <b-alert v-if="imageError" variant="warning" show class="mt-2">
            <i class="fa fa-exclamation-triangle"></i>
            Não foi possível carregar a imagem. Verifique a URL.
          </b-alert>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  getCourses,
  createCourse,
  updateCourse,
  deleteCourse,
} from "@/api/ead";

export default {
  name: "GerenciarCursos",
  data() {
    return {
      courses: [],
      loading: false,
      modalVisible: false,
      editingCourse: null,
      imageError: false,
      search: "",
      filterCategory: "",
      currentPage: 1,
      perPage: 10,
      form: {
        nome: "",
        subtitulo: "",
        descricao: "",
        imagem: "",
        categoria: "",
        tags: [],
        certificateValidity: "lifetime",
      },
      formErrors: {},
      categorias: [
        { value: "", text: "Selecione uma categoria" },
        { value: "Programação", text: "Programação" },
        { value: "Design", text: "Design" },
        { value: "Marketing", text: "Marketing" },
        { value: "Negócios", text: "Negócios" },
        { value: "Desenvolvimento Pessoal", text: "Desenvolvimento Pessoal" },
        { value: "Fotografia", text: "Fotografia" },
        { value: "Música", text: "Música" },
        { value: "Idiomas", text: "Idiomas" },
        { value: "Outro", text: "Outro" },
      ],
      validityOptions: [
        { value: "3_months", text: "3 meses" },
        { value: "6_months", text: "6 meses" },
        { value: "1_year", text: "1 ano" },
        { value: "lifetime", text: "Vitalício" },
      ],
      fields: [
        { key: "nome", label: "Nome", sortable: true },
        {
          key: "subtitulo",
          label: "Subtítulo",
          sortable: true,
          class: "subtitle-column",
        },
        { key: "categoria", label: "Categoria", sortable: true },
        { key: "createdBy", label: "Criado por" },
        { key: "actions", label: "Ações", class: "text-right" },
      ],
    };
  },
  computed: {
    canManage() {
      // Verificar se o usuário tem permissão 'ead.manage'
      const permissions = this.$store.state.user?.profile?.permissions?.ead;
      const role = this.$store.state.user?.profile?.role;
      return permissions?.manage || role === "root" || role === "company-root";
    },
    filteredCourses() {
      let filtered = this.courses;

      // Filtrar por busca
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(
          (course) =>
            course.nome?.toLowerCase().includes(searchLower) ||
            course.categoria?.toLowerCase().includes(searchLower) ||
            course.tags?.some((tag) => tag.toLowerCase().includes(searchLower))
        );
      }

      // Filtrar por categoria
      if (this.filterCategory) {
        filtered = filtered.filter(
          (course) => course.categoria === this.filterCategory
        );
      }

      return filtered;
    },
    totalCourses() {
      return this.filteredCourses.length;
    },
    paginationInfo() {
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCourses);
      return `${start}-${end}`;
    },
    categoryOptions() {
      const options = [{ value: "", text: "Todas as categorias" }];
      const uniqueCategories = [
        ...new Set(this.courses.map((c) => c.categoria)),
      ];
      uniqueCategories.forEach((cat) => {
        if (cat) options.push({ value: cat, text: cat });
      });
      return options;
    },
  },
  mounted() {
    this.loadCourses();
  },
  methods: {
    rowClass(item) {
      if (!item) return "";
      return "course-row";
    },
    clearSearch() {
      this.search = "";
      this.currentPage = 1;
    },
    formatCreatedBy(createdBy) {
      if (!createdBy) return "N/A";
      if (typeof createdBy === "string" && /^[a-f\d]{24}$/i.test(createdBy)) {
        return "N/A";
      }
      if (typeof createdBy === "string") return createdBy;
      return createdBy.name || createdBy.email || "N/A";
    },
    async loadCourses() {
      this.loading = true;
      try {
        const response = await getCourses();
        this.courses = response.data || [];
      } catch (error) {
        console.error("Erro ao carregar cursos:", error);
        this.$bvToast.toast("Erro ao carregar cursos. Tente novamente.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.loading = false;
      }
    },
    handleAdd() {
      this.editingCourse = null;
      this.resetForm();
      this.modalVisible = true;
    },
    handleEdit(course) {
      this.editingCourse = course;
      this.form = {
        nome: course.nome,
        subtitulo: course.subtitulo,
        descricao: course.descricao,
        imagem: course.imagem,
        categoria: course.categoria,
        tags: course.tags || [],
        certificateValidity: course.certificateValidity || "lifetime",
      };
      this.modalVisible = true;
    },
    handleConfigure(course) {
      // Redirecionar para a página de configuração
      this.$router.push({
        path: `/ead/cursos/${course._id}/editar`,
        query: { tab: "modules" },
      });
    },
    confirmDelete(course) {
      this.$bvModal
        .msgBoxConfirm(
          `Tem certeza que deseja deletar o curso "${course.nome}"? Esta ação não pode ser desfeita.`,
          {
            title: "Confirmar Exclusão",
            size: "md",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: "Sim, deletar",
            cancelTitle: "Cancelar",
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.handleDelete(course._id);
          }
        })
        .catch((err) => {
          console.error("Erro no modal:", err);
        });
    },
    async handleDelete(courseId) {
      this.loading = true;
      try {
        await deleteCourse(courseId);
        this.$bvToast.toast("Curso deletado com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
        await this.loadCourses();
      } catch (error) {
        console.error("Erro ao deletar curso:", error);
        this.$bvToast.toast("Erro ao deletar curso. Tente novamente.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.loading = false;
      }
    },
    async handleSubmit(event) {
      if (event) event.preventDefault();

      this.formErrors = {};
      this.loading = true;

      try {
        if (this.editingCourse) {
          await updateCourse(this.editingCourse._id, this.form);
          this.$bvToast.toast("Curso atualizado com sucesso!", {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          });
        } else {
          await createCourse(this.form);
          this.$bvToast.toast("Curso criado com sucesso!", {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          });
        }
        this.modalVisible = false;
        await this.loadCourses();
      } catch (error) {
        console.error("Erro ao salvar curso:", error);
        this.$bvToast.toast(
          error.response?.data?.message ||
            "Erro ao salvar curso. Tente novamente.",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
        this.formErrors = error.response?.data?.errors || {};
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form = {
        nome: "",
        subtitulo: "",
        descricao: "",
        imagem: "",
        categoria: "",
        tags: [],
        certificateValidity: "lifetime",
      };
      this.formErrors = {};
      this.imageError = false;
      this.editingCourse = null;
    },
    debouncedSearch() {
      // Debounce simples para busca
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1; // Resetar para primeira página ao buscar
      }, 300);
    },
  },
};
</script>

<style scoped>
.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
}

.search-filter {
  flex: 1;
}

.category-filter {
  flex-basis: 200px;
}

.action-button {
  flex-basis: 150px;
}

.filters-section .form-group {
  margin-bottom: 0;
}

.filters-section .form-control,
.filters-section .custom-select {
  border-radius: 6px;
  border: 1px solid #e9ecef;
  background-color: #fff;
  color: #495057;
  height: calc(1.5em + 1rem + 2px);
  padding: 0.5rem 1rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.filters-section .form-control:focus,
.filters-section .custom-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
  outline: 0;
}

.filters-section .input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-right: 0;
  border-radius: 6px 0 0 6px;
}

.filters-section .form-group label {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #495057;
  margin-bottom: 0.5rem;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.pagination-nav {
  margin-top: 1.5rem;
}

::v-deep .courses-header th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
  font-size: 0.8rem;
  text-transform: uppercase;
  border: 0;
  white-space: nowrap;
}

::v-deep .course-row {
  cursor: pointer;
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.2s ease-in-out;
}

::v-deep .course-row:hover {
  background-color: #e9ecef !important;
}

::v-deep tbody tr:last-of-type {
  border-bottom: none;
}

::v-deep .course-row td {
  border-top: none;
  vertical-align: middle;
  padding: 0.85rem 0.75rem;
}

::v-deep .subtitle-column {
  width: 22%;
}

::v-deep .course-name {
  color: #007bff;
  font-weight: 500;
}

.subtitle-cell {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #4b5563;
}

.category-text {
  color: #334155;
  font-weight: 500;
}

::v-deep .creator-text {
  color: #6c757d;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.action-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  margin-left: 8px;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
}

.action-btn i {
  font-size: 0.95rem;
}

.action-btn:active {
  transform: scale(0.95);
}

.action-btn:focus {
  outline: none;
}

.action-btn-secondary {
  background-color: #f1f5f9;
  color: #475569;
}

.action-btn-secondary:hover {
  background-color: #475569;
  color: #fff;
}

.action-btn-primary {
  background-color: #e0e7ff;
  color: #4338ca;
}

.action-btn-primary:hover {
  background-color: #4338ca;
  color: #fff;
}

.action-btn-danger {
  background-color: #fee2e2;
  color: #dc2626;
}

.action-btn-danger:hover {
  background-color: #dc2626;
  color: #fff;
}

::v-deep .page-item .page-link {
  border-radius: 6px;
  margin: 0 3px;
  border: 1px solid #e9ecef;
  color: #495057;
  transition: background-color 0.2s, color 0.2s;
}

::v-deep .page-item .page-link:hover {
  background-color: #f0f2f5;
  color: #0056b3;
}

::v-deep .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

::v-deep .page-item.disabled .page-link {
  background-color: transparent;
  color: #adb5bd;
}

::v-deep .card {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

::v-deep .card-header {
  border-radius: 8px 8px 0 0;
}

::v-deep .card-body {
  padding: 1.5rem;
}

/* Responsive */
@media (max-width: 768px) {
  .filters-section {
    flex-direction: column;
  }

  .search-filter,
  .category-filter,
  .action-button {
    flex-basis: auto;
    width: 100%;
  }

  .subtitle-cell {
    max-width: 180px;
  }

  .action-buttons {
    justify-content: flex-start;
  }
}
</style>
